/**
 * MyProfilePage — Coordonnées personnelles uniquement.
 *
 * Cette page gère STRICTEMENT les coordonnées personnelles & privées de l'utilisateur,
 * visibles uniquement par l'administration. Les coordonnées cabinet/professionnelles
 * sont gérées dans "Mes Fiches" (/mes-fiches).
 */
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save, Loader2, Mail, Phone, User, Camera, IdCard,
  Lock, ChevronRight, Building2, FileText, Shield, Info,
} from 'lucide-react';
import { API } from '@/App';

const MyProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ficheCount, setFicheCount] = useState(null);
  const photoInputRef = useRef(null);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',          // téléphone personnel (privé)
    bio: '',            // courte présentation (peut être réutilisée dans les fiches publiques)
    rpps: '',
    profile_image: '',
  });

  useEffect(() => { fetchProfile(); fetchFicheCount(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/users/me`);
      setProfile(res.data);
      setForm({
        first_name: res.data.first_name || '',
        last_name: res.data.last_name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        bio: res.data.bio || '',
        rpps: res.data.rpps || '',
        profile_image: res.data.profile_image || '',
      });
    } catch (err) {
      // Resilience: in production the backend may temporarily not expose
      // /users/me (deployment lag). Fall back to legacy /auth/me, then to
      // the cached user from localStorage so the UI keeps working.
      const status = err?.response?.status;
      if (status === 404 || status === 405) {
        try {
          const res2 = await axios.get(`${API}/auth/me`);
          setProfile(res2.data);
          setForm({
            first_name: res2.data.first_name || '',
            last_name: res2.data.last_name || '',
            email: res2.data.email || '',
            phone: res2.data.phone || '',
            bio: res2.data.bio || '',
            rpps: res2.data.rpps || '',
            profile_image: res2.data.profile_image || '',
          });
          setLoading(false);
          return;
        } catch {}
      }
      const cached = JSON.parse(localStorage.getItem('user') || 'null');
      if (cached) {
        setProfile(cached);
        setForm({
          first_name: cached.first_name || '',
          last_name: cached.last_name || '',
          email: cached.email || '',
          phone: cached.phone || '',
          bio: cached.bio || '',
          rpps: cached.rpps || '',
          profile_image: cached.profile_image || '',
        });
        toast.warning('Profil chargé depuis le cache local. Certaines modifications peuvent être indisponibles.');
      } else {
        toast.error('Impossible de charger le profil');
      }
    }
    setLoading(false);
  };

  const fetchFicheCount = async () => {
    try {
      const res = await axios.get(`${API}/my-fiches`);
      setFicheCount(Array.isArray(res.data) ? res.data.length : 0);
    } catch { setFicheCount(0); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Seuls les champs PERSONNELS sont envoyés ici.
      const payload = {
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        bio: form.bio,
        rpps: form.rpps,
        profile_image: form.profile_image,
      };
      let res;
      try {
        res = await axios.put(`${API}/users/me`, payload);
      } catch (err) {
        // Fallback in case the production backend hasn't been redeployed yet
        if (err?.response?.status === 404 || err?.response?.status === 405) {
          res = await axios.put(`${API}/auth/update-profile`, payload);
        } else {
          throw err;
        }
      }
      setProfile(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      toast.success('Coordonnées personnelles mises à jour');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    }
    setSaving(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/profile-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, profile_image: res.data.url }));
      toast.success('Photo mise à jour. N\'oubliez pas d\'enregistrer.');
    } catch {
      toast.error('Erreur d\'upload de la photo');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="my-profile-loading">
        <Loader2 className="animate-spin text-[#35AFE4]" size={32} />
      </div>
    );
  }

  const role = profile?.role;
  const isOphthalmologist = role === 'ophthalmologist';
  const isIntern = role === 'intern';
  const showFichesCTA = isOphthalmologist || role === 'super_admin' || role === 'regional';

  return (
    <div className="min-h-full bg-[#f8fafb]" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="my-profile-page">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>
            Mes coordonnées personnelles
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Informations privées de votre profil — visibles uniquement par vous et l'administration SFOALC.
          </p>

          {/* Privacy banner */}
          <div className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-50 border border-amber-100 text-[12px] text-amber-900 leading-snug" data-testid="profile-privacy-banner">
            <Lock size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p>
              <strong>Confidentialité :</strong> ces coordonnées <strong>ne sont jamais affichées</strong> dans l'annuaire public ni sur vos fiches.
              Pour gérer vos <strong>coordonnées professionnelles</strong> (adresse cabinet, téléphone consultation, horaires…),
              utilisez la section <em>Mes fiches praticien</em>.
            </p>
          </div>
        </div>

        {/* Identity + photo */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5" data-testid="identity-card">
          <h2 className="text-base font-bold text-[#1e3a5f] mb-4 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
            <User size={18} className="text-[#35AFE4]" />
            Identité
          </h2>

          <div className="flex flex-col sm:flex-row gap-5 items-start">
            {/* Avatar */}
            <div className="flex flex-col items-center gap-2">
              <div className="relative">
                {form.profile_image ? (
                  <img src={form.profile_image} alt="Profil" className="w-24 h-24 rounded-full object-cover ring-4 ring-[#eaf6fc]" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white text-2xl font-bold ring-4 ring-[#eaf6fc]">
                    {form.first_name?.[0]}{form.last_name?.[0]}
                  </div>
                )}
                <button onClick={() => photoInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[#35AFE4] hover:bg-[#1e9bd4] text-white flex items-center justify-center shadow-md transition-colors"
                  data-testid="upload-photo-btn"
                  aria-label="Changer la photo">
                  <Camera size={14} />
                </button>
                <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
              </div>
              <span className="text-[11px] text-gray-400">Photo de profil</span>
            </div>

            {/* Names + RPPS */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
              <Field label="Prénom *" value={form.first_name} onChange={v => setForm(f => ({ ...f, first_name: v }))} testId="input-firstname" />
              <Field label="Nom *" value={form.last_name} onChange={v => setForm(f => ({ ...f, last_name: v }))} testId="input-lastname" />
              {(isOphthalmologist || isIntern) && (
                <div className="sm:col-span-2">
                  <Field label={isIntern ? 'N° d\'identification' : 'Numéro RPPS'}
                    value={form.rpps}
                    onChange={v => setForm(f => ({ ...f, rpps: v }))}
                    placeholder="11 chiffres" icon={IdCard} testId="input-rpps" />
                  <p className="mt-1 text-[11px] text-gray-400">Information confidentielle utilisée pour la validation par le bureau SFOALC.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contact info */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5" data-testid="contact-card">
          <h2 className="text-base font-bold text-[#1e3a5f] mb-1 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
            <Mail size={18} className="text-[#35AFE4]" />
            Contact personnel
          </h2>
          <p className="text-[11px] text-gray-400 mb-4 flex items-center gap-1.5">
            <Shield size={12} /> Visibles uniquement par l'administration SFOALC.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">Email</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="email" value={form.email} disabled
                  className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500 cursor-not-allowed"
                  data-testid="input-email" />
              </div>
              <p className="mt-1 text-[10px] text-gray-400">Pour modifier votre email, contactez l'administration.</p>
            </div>
            <Field label="Téléphone personnel"
              icon={Phone}
              value={form.phone}
              onChange={v => setForm(f => ({ ...f, phone: v }))}
              placeholder="06 12 34 56 78"
              testId="input-phone-perso" />
          </div>
        </div>

        {/* Short bio */}
        <div className="bg-white rounded-xl border border-gray-100 p-5 mb-5" data-testid="bio-card">
          <h2 className="text-base font-bold text-[#1e3a5f] mb-4 flex items-center gap-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
            <FileText size={18} className="text-[#35AFE4]" />
            Présentation
          </h2>
          <textarea
            value={form.bio}
            onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
            rows={4}
            placeholder="Quelques lignes pour vous présenter (parcours, spécialités, philosophie…). Vous pourrez la réutiliser dans vos fiches publiques."
            className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none transition-all resize-none"
            data-testid="input-bio"
          />
          <p className="mt-2 text-[11px] text-gray-400 flex items-start gap-1.5">
            <Info size={12} className="mt-0.5 flex-shrink-0" />
            Cette présentation est confidentielle par défaut. Elle pourra être copiée dans vos fiches praticien si vous le souhaitez.
          </p>
        </div>

        {/* Save button */}
        <div className="flex justify-end mb-8">
          <button onClick={handleSave} disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#35AFE4] hover:bg-[#1e9bd4] text-white text-sm font-bold rounded-lg transition-colors disabled:opacity-50"
            data-testid="save-profile-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Enregistrer mes coordonnées
          </button>
        </div>

        {/* Fiches CTA (only for ophthalmologists / admins) */}
        {showFichesCTA && (
          <div
            className="relative overflow-hidden rounded-2xl p-6 cursor-pointer group transition-all hover:shadow-lg"
            style={{ background: 'linear-gradient(135deg, #eaf6fc 0%, #f0f8fb 100%)', border: '1px solid #d0e8f0' }}
            onClick={() => navigate('/mes-fiches')}
            data-testid="cta-fiches"
          >
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-[#35AFE4]/10 -translate-y-12 translate-x-12 group-hover:scale-110 transition-transform" />
            <div className="relative z-10 flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-white text-[#35AFE4] flex items-center justify-center flex-shrink-0 shadow-sm">
                <Building2 size={22} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>
                    {ficheCount === 0 ? "Créer votre première fiche praticien" : "Gérer mes fiches praticien"}
                  </h3>
                  {ficheCount !== null && ficheCount > 0 && (
                    <span className="px-2 py-0.5 bg-white text-[#35AFE4] text-[11px] font-bold rounded-full">{ficheCount} fiche{ficheCount > 1 ? 's' : ''}</span>
                  )}
                </div>
                <p className="text-xs text-gray-600 max-w-lg leading-relaxed">
                  {ficheCount === 0
                    ? "Vous n'avez pas encore de fiche publique dans l'annuaire SFOALC. Créez votre première fiche cabinet : adresse, téléphone de consultation, horaires, spécialités… Vous pouvez créer plusieurs fiches si vous exercez sur plusieurs sites."
                    : "Modifiez les coordonnées de vos cabinets, vos horaires, vos spécialités. Vous pouvez créer une fiche supplémentaire pour chaque cabinet où vous exercez."}
                </p>
              </div>
              <Link to="/mes-fiches"
                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 bg-[#35AFE4] hover:bg-[#1e9bd4] text-white text-xs font-bold rounded-full transition-colors flex-shrink-0"
                data-testid="cta-fiches-btn">
                {ficheCount === 0 ? 'Créer ma fiche' : 'Mes fiches'} <ChevronRight size={13} />
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Helpers
// ============================================================
const Field = ({ label, value, onChange, placeholder, icon: Icon, testId }) => (
  <div>
    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5 block">{label}</label>
    <div className="relative">
      {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full ${Icon ? 'pl-9' : 'pl-3'} pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#35AFE4]/20 focus:border-[#35AFE4] outline-none transition-all`}
        data-testid={testId}
      />
    </div>
  </div>
);

export default MyProfilePage;
